import { memo, type SyntheticEvent, useCallback, useState } from 'react';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import { WORKSPACE_AGENT_PANEL_REMOVE_BACKGROUND_PREVIEW } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import {
  getWorkspaceAgentGuidedVariantCanvasColor,
  WORKSPACE_AGENT_GUIDED_VARIANT_CHECKERBOARD_STYLE,
  type WorkspaceAgentGuidedVariantColorOptionId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantColor';
import { getWorkspaceAgentRemoveBackgroundPreviewOval } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentRemoveBackground';

type MeasuredPreviewSize = {
  height: number;
  url: string;
  width: number;
};

export type WorkspaceAgentGuidedVariantRemoveBackgroundPreviewProps = {
  backgroundId: WorkspaceAgentGuidedVariantColorOptionId;
  customColor: string;
  imageAlt: string;
  previewHeight: number | null;
  previewImageUrl: string | null;
  previewWidth: number | null;
};

export const WorkspaceAgentGuidedVariantRemoveBackgroundPreview = memo(
  ({
    backgroundId,
    customColor,
    imageAlt,
    previewHeight,
    previewImageUrl,
    previewWidth,
  }: WorkspaceAgentGuidedVariantRemoveBackgroundPreviewProps) => {
    const [measuredSize, setMeasuredSize] = useState<MeasuredPreviewSize | null>(null);
    const canvasColor = getWorkspaceAgentGuidedVariantCanvasColor(backgroundId, customColor);
    const measuredForCurrentImage = measuredSize?.url === previewImageUrl ? measuredSize : null;
    const oval = getWorkspaceAgentRemoveBackgroundPreviewOval(
      previewWidth ?? measuredForCurrentImage?.width ?? null,
      previewHeight ?? measuredForCurrentImage?.height ?? null,
    );
    const maskImage = `radial-gradient(ellipse ${oval.radiusX}% ${oval.radiusY}% at center, #000 62%, transparent 100%)`;

    const handleImageLoad = useCallback(
      (event: SyntheticEvent<HTMLImageElement>) => {
        const { naturalHeight, naturalWidth } = event.currentTarget;

        if (!previewImageUrl || !naturalWidth || !naturalHeight) {
          return;
        }

        setMeasuredSize({ height: naturalHeight, url: previewImageUrl, width: naturalWidth });
      },
      [previewImageUrl],
    );

    return (
      <div
        className="size-20 shrink-0 overflow-hidden rounded-12"
        data-background-color={canvasColor ?? 'transparent'}
        data-background-id={backgroundId}
        data-oval-x={oval.radiusX}
        data-oval-y={oval.radiusY}
        data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_REMOVE_BACKGROUND_PREVIEW"
        style={canvasColor ? { backgroundColor: canvasColor } : WORKSPACE_AGENT_GUIDED_VARIANT_CHECKERBOARD_STYLE}
      >
        {previewImageUrl ? (
          // Equal radii on this square well are a circle. Scale the short axis to the photo.
          <div className="size-full" style={{ maskImage, WebkitMaskImage: maskImage }}>
            <WorkspaceAgentPanelImage
              {...WORKSPACE_AGENT_PANEL_REMOVE_BACKGROUND_PREVIEW}
              alt={imageAlt}
              className="size-full object-contain"
              onLoad={handleImageLoad}
              src={previewImageUrl}
            />
          </div>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantRemoveBackgroundPreview.displayName = 'WorkspaceAgentGuidedVariantRemoveBackgroundPreview';
